import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";

const Story = () => {
  const { t, lang } = useLang();

  const chapters = lang === "fr"
    ? [
        { n: "I", title: "Le pont", body: "Maison Marigold naît d'un voyage. D'une rencontre, à Bhuj, avec une tisserande qui, en silence, faisait passer six mille fils de chaîne dans son métier. À cet instant, nous avons compris : un objet artisanal n'est pas un produit. C'est une voix." },
        { n: "II", title: "La méthode", body: "Nous ne passons par aucun intermédiaire. Nous voyageons. Nous écoutons. Nous photographions les mains, jamais seulement les pièces. Quand un atelier nous accueille, nous restons. Nous payons à la commande, non à la vente. Nous achetons par séries petites pour ne jamais épuiser un savoir." },
        { n: "III", title: "Le voyage", body: "De Mumbai, les pièces traversent l'océan dans des conteneurs de bois certifiés FSC. Elles arrivent au Havre, puis à notre atelier parisien — une cour pavée dans le 11ᵉ. Chaque pièce est dépoussiérée, photographiée, accompagnée d'une fiche manuscrite signée par l'artisan." },
        { n: "IV", title: "Chez vous", body: "L'emballage est en papier recyclé, scellé d'un cachet de cire de soja. Aucun plastique. À l'ouverture, vous découvrez une lettre — l'histoire de votre pièce, le visage de celui qui l'a faite. Le fil se prolonge : un email, et vous pourrez écrire à l'atelier." },
      ]
    : [
        { n: "I", title: "The bridge", body: "Maison Marigold was born of a journey. Of a meeting, in Bhuj, with a weaver who, in silence, was threading six thousand warp threads through her loom. In that moment, we understood: a handcrafted object is not a product. It is a voice." },
        { n: "II", title: "The method", body: "We use no middlemen. We travel. We listen. We photograph hands, never just the pieces. When a workshop welcomes us, we stay. We pay on order, not on sale. We buy in small batches so as never to exhaust a craft." },
        { n: "III", title: "The journey", body: "From Mumbai, the pieces cross the ocean in FSC-certified wooden containers. They arrive at Le Havre, then at our Paris atelier — a cobbled courtyard in the 11th. Each piece is dusted, photographed, and paired with a handwritten note signed by the artisan." },
        { n: "IV", title: "At home", body: "Packaging is in recycled paper, sealed with a soy-wax stamp. No plastic. On opening, you discover a letter — the story of your piece, the face of the one who made it. The thread continues: a single email, and you can write to the workshop." },
      ];

  return (
    <div className="pt-28 md:pt-36 pb-24" data-testid="page-story">
      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-10 items-end mb-24 md:mb-32">
        <div className="lg:col-span-7">
          <p className="overline mb-5">{t.storyBlock.overline}</p>
          <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl font-light text-[#1E2B3C] leading-[1.02]">
            {lang === "fr" ? "Un fil tendu," : "A thread stretched,"}
            <br />
            <span className="italic text-[#B34A36]">
              {lang === "fr" ? "deux continents." : "two continents."}
            </span>
          </h1>
        </div>
        <div className="lg:col-span-5 text-[#5C5C5C] leading-relaxed">
          <p className="font-serif-display italic text-2xl text-[#1E2B3C] mb-4">
            « Vasudhaiva Kutumbakam »
          </p>
          <p>{t.storyBlock.body1}</p>
        </div>
      </section>

      {/* Image strip */}
      <section className="grid md:grid-cols-3 gap-1 mb-24 md:mb-32 px-1">
        {[
          "/brand/artisan-hands.jpg",
          "https://images.pexels.com/photos/57565/pexels-photo-57565.jpeg?auto=compress&cs=tinysrgb&w=900",
          "https://images.pexels.com/photos/33753646/pexels-photo-33753646.jpeg?auto=compress&cs=tinysrgb&w=900",
        ].map((src, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.1 }}
            className="aspect-[4/5] overflow-hidden grain"
          >
            <img src={src} alt="" className="w-full h-full object-cover" />
          </motion.div>
        ))}
      </section>

      {/* Chapters */}
      <section className="px-6 md:px-12 lg:px-24 relative">
        <div className="max-w-3xl mx-auto space-y-20 md:space-y-28">
          {chapters.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="grid grid-cols-12 gap-6"
              data-testid={`story-chapter-${i + 1}`}
            >
              <div className="col-span-2 md:col-span-2">
                <p className="font-serif-display italic text-5xl md:text-6xl text-[#B34A36]">{c.n}</p>
              </div>
              <div className="col-span-10 md:col-span-10">
                <h2 className="font-serif-display text-3xl md:text-4xl font-light text-[#1E2B3C]">
                  {c.title}
                </h2>
                <p className="mt-5 text-[#1A1A1A]/85 leading-[1.85] max-w-2xl">{c.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mt-32 text-center px-6">
        <p className="font-serif-display italic text-3xl md:text-4xl text-[#1E2B3C] max-w-2xl mx-auto">
          {lang === "fr"
            ? "« Un objet qui voyage emporte sa terre avec lui. »"
            : "\"An object that travels carries its earth with it.\""}
        </p>
        <Link to="/shop" className="btn-primary mt-10 inline-flex" data-testid="story-shop-cta">
          {t.hero.cta}
        </Link>
      </section>
    </div>
  );
};

export default Story;
